import { useRef, useState, useEffect, useMemo, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addDays } from 'date-fns';
import { useIntersection } from 'react-use';
import { Button } from '@c2fo/liquidity';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import useInstantPayEligible from '@/enterprise/components/InstantPay/useInstantPayEligible';
import FinalApprovalModal from '@/enterprise/components/OfferBuilder/OfferReview/FinalApprovalModal';
import PageBottomPortal from '@/enterprise/components/PageBottomPortal/PageBottomPortal';
import { useConfirmAgreementsWhenRequired } from '@/enterprise/data/useAgreements';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import useUpdateIncludeNewBuyer from '@/enterprise/data/useUpdateIncludeNewBuyer';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';
import { IntercomDataTargetNames } from '@/reporting/intercomDataTargetNames';
import { ButtonLink } from '@/shared/components/ButtonLink';
import useExchangeRates from '@/shared/data/useExchangeRates';
import useIncludeNewBuyerState from '@/shared/data/useIncludeNewBuyerState';
import convertCurrency from '@/utils/convertCurrency';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useLocaleFormat from '@/utils/useLocaleFormat';
import BlendedRateDisplay from '../BlendedRateDisplay';
import NavigationBar from '../NavigationBar';
import {
  OfferBuilderLocation,
  useOfferBuilderContext,
  type OfferBuilderVariant,
  type AcquisitionOfferProperties,
} from '../OfferBuilderContext';
import { useRateCalculations } from '../useRateCalculations';
import {
  buildTakerMarketKey,
  calculateBlendedRate,
  calculateDiscount,
  computeAggregateSubmitStats,
  computeMarketSubmitStats,
  trackOfferSubmitEvent,
} from '../utils';
import BuyerLogoList from './BuyerLogoList';
import ConfirmAgreements from './ConfirmAgreements';
import IncludeNewBuyer from './IncludeNewBuyer';
import OfferSubmitErrorModal from './OfferSubmitErrorModal';
import PreferredAutoRenewal, { RENEWAL_TERM_DAYS } from './PreferredAutoRenewal';
import useSubmitDraftOffer from './useSubmitDraftOffer/useSubmitDraftOffer';
import { buildDraftInput } from './useSubmitDraftOffer/utils';
import useSubmitOffers from './useSubmitOffers/useSubmitOffers';
import { mapSelectedToSubmitValues } from './useSubmitOffers/utils';
import useSubmitPreferredTermOffer from './useSubmitPreferredTermOffer/useSubmitPreferredTermOffer';

const trackBulkOfferStats = ({
  stats,
  selectedCurrency,
  exchangeRates,
  location,
  source,
  acquisitionOfferProperties,
  submissionDurationMs,
  takerMarketCount,
  trackEnterpriseEvent,
  builderVariant,
  submissionBatchId,
  preferredRateUpgrade,
  blendedRate,
  blendedRateType,
}: {
  stats: {
    discount: number;
    estimatedEarlyPaymentAmount: number;
    eligibleInvoiceAmount: number;
    invoiceCount: number;
    uniqueMakerOrganizationCount: number;
  };
  selectedCurrency: string;
  exchangeRates: ReturnType<typeof useExchangeRates>['data'];
  location?: OfferBuilderLocation;
  source?: string;
  acquisitionOfferProperties?: AcquisitionOfferProperties;
  submissionDurationMs: number;
  takerMarketCount: number;
  trackEnterpriseEvent: ReturnType<typeof useReporting>['trackEnterpriseEvent'];
  builderVariant: OfferBuilderVariant;
  submissionBatchId: string;
  preferredRateUpgrade: boolean;
  blendedRate: number;
  blendedRateType: 'APR' | 'DISC';
}) => {
  const submitStats = computeAggregateSubmitStats({
    eligibleInvoiceAmount: stats.eligibleInvoiceAmount,
    discount: stats.discount,
    estimatedEarlyPaymentAmount: stats.estimatedEarlyPaymentAmount,
    invoiceCount: stats.invoiceCount,
    currency: selectedCurrency,
    exchangeRates,
  });

  trackEnterpriseEvent('bulk-offer::stats', {
    referrer: 'offer-builder',
    builderVariant,
    buyerCount: stats.uniqueMakerOrganizationCount,
    submissionDurationMs,
    submissionBatchId,
    takerMarketCount,
    preferredRateUpgrade,
    blendedRate,
    blendedRateType,
    ...submitStats,
    ...(location && { location }),
    ...(source && { source }),
    ...(acquisitionOfferProperties?.urlReferrer && { urlReferrer: acquisitionOfferProperties.urlReferrer }),
    ...(acquisitionOfferProperties?.acquisitionCorrelationId && {
      acquisitionCorrelationId: acquisitionOfferProperties.acquisitionCorrelationId,
    }),
  });
};

const OfferSubmit = ({
  offerSubmitting,
  setOfferSubmitting,
}: {
  offerSubmitting: boolean;
  setOfferSubmitting: (value: boolean) => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addError } = useDataDog();
  const { selectedCurrency } = useCurrencyContext();
  const { trackEnterpriseEvent } = useReporting();
  const { data: exchangeRates } = useExchangeRates();
  const { asCurrency } = useLocaleFormat();
  const {
    state: {
      builderVariant,
      frequency,
      selected,
      schedule,
      draftOfferProperties,
      workingTakerMarkets,
      disableNavigation,
      location,
      source,
      acquisitionOfferProperties,
      preferredRateSelected,
      preUpgradeRates,
      rateType: rateTypeFromContext,
    },
    dispatch,
  } = useOfferBuilderContext();
  const { calculateEffectiveRates } = useRateCalculations();
  const effectiveRates = calculateEffectiveRates();
  const { submitOffers } = useSubmitOffers();
  const { isInstantPayEligible } = useInstantPayEligible();
  const { mutateAsync: submitDraftOffer } = useSubmitDraftOffer();
  const { mutateAsync: submitPreferredTermOffer } = useSubmitPreferredTermOffer();
  // agreements
  const [isAgreementsChecked, setIsAgreementsChecked] = useState(false);
  const [hasAgreementsError, setHasAgreementsError] = useState(false);
  const { validateAndConfirmAgreementsConditionally } = useConfirmAgreementsWhenRequired(selected);
  // include new buyer
  const { mutateAsync: updateIncludeNewBuyer } = useUpdateIncludeNewBuyer();
  const showIncludeNewBuyer = builderVariant === 'standard' && frequency === 'recurring';
  const includeNewBuyerState = useIncludeNewBuyerState({
    enabled: showIncludeNewBuyer,
  });
  const {
    isChecked: isIncludeNewBuyerChecked = false,
    setIsChecked: setIsIncludeNewBuyerChecked = () => {
      // no-op
    },
    shouldSubmitUpdate = () => false,
  } = showIncludeNewBuyer ? includeNewBuyerState : {};
  // auto renewal
  const [autoRenewalChecked, setAutoRenewalChecked] = useState(true);
  const intersectionRef = useRef<HTMLDivElement>(null);
  const [isAboveViewport, setIsAboveViewport] = useState(false);
  const [isFinalApprovalModalOpen, setIsFinalApprovalModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  // react-use's types still expect RefObject<HTMLElement> (non-null); React 19's useRef(null) is correctly RefObject<HTMLElement | null>
  const intersection = useIntersection(intersectionRef as RefObject<HTMLDivElement>, {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  });
  const isAcquisitionVariant = builderVariant === 'acquisition';

  const ratesByMarket = useMemo(
    () => new Map(effectiveRates.map((item) => [item.takerMarket, { rate: item.rate, rateType: item.rateType }])),
    [effectiveRates]
  );

  const displayRateType = rateTypeFromContext ?? 'APR';

  const calculateStatsForMarkets = useMemo(() => {
    return (markets: typeof selected) => {
      const uniqueMakerOrganizationUuids = new Set<string>();

      const stats = markets.reduce(
        (acc, takerMarket) => {
          const rateInfo = ratesByMarket.get(takerMarket);

          if (!rateInfo) {
            return acc;
          }

          const { rate, rateType } = rateInfo;

          uniqueMakerOrganizationUuids.add(takerMarket.makerOrganizationUuid);

          const convertedEligibleInvoiceAmount = convertCurrency({
            amount: takerMarket.eligibleInvoiceAmount,
            exchangeRates,
            from: takerMarket.currency,
            to: selectedCurrency,
          });
          const discount = convertCurrency({
            amount: calculateDiscount({
              dpeWeightedAvg: takerMarket.eligibleDpeWeightedAvg,
              invoiceAmount: takerMarket.eligibleInvoiceAmount,
              isDiscount: rateType === 'DISC',
              rate: rate ?? 0,
            }),
            exchangeRates,
            from: takerMarket.currency,
            to: selectedCurrency,
          });

          acc.discount += discount;
          acc.estimatedEarlyPaymentAmount += convertedEligibleInvoiceAmount - discount;
          acc.eligibleInvoiceAmount += convertedEligibleInvoiceAmount;
          acc.invoiceCount += takerMarket.eligibleInvoiceCount;
          return acc;
        },
        { discount: 0, estimatedEarlyPaymentAmount: 0, eligibleInvoiceAmount: 0, invoiceCount: 0 }
      );

      return {
        ...stats,
        uniqueMakerOrganizationCount: uniqueMakerOrganizationUuids.size,
      };
    };
  }, [ratesByMarket, exchangeRates, selectedCurrency]);

  const calculateBlendedRateForMarkets = useMemo(() => {
    return (markets: typeof selected) => {
      const items = markets.flatMap((takerMarket) => {
        const rateInfo = ratesByMarket.get(takerMarket);
        if (!rateInfo || rateInfo.rate === undefined) {
          return [];
        }

        return [
          {
            dpeWeightedAvg: takerMarket.eligibleDpeWeightedAvg,
            dpeWeightedSum: takerMarket.eligibleDpeWeightedSum,
            invoiceAmount: takerMarket.eligibleInvoiceAmount,
            isDiscount: rateInfo.rateType === 'DISC',
            rate: rateInfo.rate,
          },
        ];
      });

      return Math.round(calculateBlendedRate({ items, rateType: displayRateType }) * 100 * 100) / 100;
    };
  }, [ratesByMarket, displayRateType]);

  const submittedOfferStats = useMemo(() => calculateStatsForMarkets(selected), [calculateStatsForMarkets, selected]);

  // disable submit button if any rate is undefined
  const disableSubmitButton = effectiveRates.some((item) => item.rate === undefined);

  // check if the submit button has scrolled above the top of the viewport
  useEffect(() => {
    const updatePosition = () => {
      if (intersectionRef.current) {
        const rect = intersectionRef.current.getBoundingClientRect();
        setIsAboveViewport(!intersection?.isIntersecting && rect.bottom <= 64); // header height is 64px
      }
    };

    if (intersection) {
      updatePosition();
      window.addEventListener('scroll', updatePosition);

      return () => window.removeEventListener('scroll', updatePosition);
    }

    return undefined;
  }, [intersection]);

  const mapSuccessToWorkingTakerMarket = (success: { marketUuid: string; takerDivisionUuid: string }) => {
    const key = buildTakerMarketKey({ marketUuid: success.marketUuid, takerDivisionUuid: success.takerDivisionUuid });
    const workingTakerMarket = workingTakerMarkets[key];

    return workingTakerMarket
      ? {
          rate: workingTakerMarket.rate,
          rateType: workingTakerMarket.rateType,
          pricingRateUuid: workingTakerMarket.pricingRateUuid,
          pricingStrategy: workingTakerMarket.pricingStrategy,
          suggestedRate: workingTakerMarket.suggestedRate,
          suggestRateType: workingTakerMarket.suggestRateType,
          customRate: workingTakerMarket.customRate,
          customRateType: workingTakerMarket.customRateType,
          currency: workingTakerMarket.currency,
          ...(workingTakerMarket.projectedClearingPercentage !== undefined && {
            projectedClearingPercentage: workingTakerMarket.projectedClearingPercentage,
          }),
        }
      : null;
  };

  const handleSubmit = async () => {
    setOfferSubmitting(true);

    try {
      await validateAndConfirmAgreementsConditionally({ isAgreementsChecked: isAgreementsChecked });
    } catch {
      setHasAgreementsError(true);
      setOfferSubmitting(false);
      return;
    }

    if (!frequency) {
      addError('Enterprise Offer Builder Submit Error', {
        error: 'Frequency must be selected before submission',
        location: 'OfferSubmit component -> handleSubmit handler',
      });
      setOfferSubmitting(false);
      return;
    }

    // Preferred rate offers (both standard and draft) require an explicit approval step before
    // submission. On first call: pause submission, open the modal, and return early.
    // When the user approves, handleSubmit is called again with the modal already open,
    // so we close it and continue through to the actual submission logic below.
    const isDraftPreferred = builderVariant === 'draft' && draftOfferProperties?.offerType === 'PREFERRED';
    const isPreferredOffer = preferredRateSelected || isDraftPreferred;

    if (isPreferredOffer) {
      if (!isFinalApprovalModalOpen) {
        setOfferSubmitting(false);
        setIsFinalApprovalModalOpen(true);
        return;
      } else {
        setIsFinalApprovalModalOpen(false);
      }
    }

    dispatch({ type: 'START_SUBMISSION' });

    if (builderVariant === 'draft' && draftOfferProperties) {
      const draftSubmitStartTime = performance.now();
      const draftSubmissionBatchId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      const draftSubmitStats = computeAggregateSubmitStats({
        eligibleInvoiceAmount: submittedOfferStats.eligibleInvoiceAmount,
        discount: submittedOfferStats.discount,
        estimatedEarlyPaymentAmount: submittedOfferStats.estimatedEarlyPaymentAmount,
        invoiceCount: submittedOfferStats.invoiceCount,
        currency: selectedCurrency,
        exchangeRates,
      });
      await submitDraftOffer(
        buildDraftInput({
          selected,
          frequency,
          draftOfferProperties,
          autoRenewalChecked,
          submitStats: draftSubmitStats,
        }),
        {
          onSuccess: () => {
            const draftSubmitEndTime = performance.now();
            const submissionDurationMs = Math.round(draftSubmitEndTime - draftSubmitStartTime);

            trackBulkOfferStats({
              stats: submittedOfferStats,
              selectedCurrency,
              exchangeRates,
              location,
              source,
              submissionDurationMs,
              takerMarketCount: selected.length,
              trackEnterpriseEvent,
              builderVariant: 'draft',
              submissionBatchId: draftSubmissionBatchId,
              preferredRateUpgrade: preferredRateSelected,
              blendedRate: calculateBlendedRateForMarkets(selected),
              blendedRateType: displayRateType,
            });

            navigate(asEnterpriseLink(`offer/${draftOfferProperties.draftOfferUuid}/confirmation`));
            dispatch({ type: 'SET_SUBMISSION_COMPLETE', payload: { errors: [] } });
          },
          onError: () => setIsErrorModalOpen(true),
        }
      );
    } else if (preferredRateSelected && builderVariant === 'standard') {
      const expirationDate = addDays(new Date(), RENEWAL_TERM_DAYS).toISOString();
      const preferredStartTime = performance.now();
      const preferredSubmissionBatchId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      // Group markets by rate + rateType — markets sharing the same rate can be submitted together.
      // Each group maps to one POST /offers/preferred-term call.
      const preferredGroupMap = new Map<
        string,
        { rate: number; rateType: 'APR' | 'DISC'; takerMarkets: TakerMarket[] }
      >();
      for (const { takerMarket, rate, rateType } of effectiveRates) {
        if (rate === undefined || rateType === undefined) continue;
        const groupKey = `${rate}-${rateType}`;
        const existing = preferredGroupMap.get(groupKey);
        if (existing) {
          existing.takerMarkets.push(takerMarket);
        } else {
          preferredGroupMap.set(groupKey, {
            rate,
            rateType: rateType as 'APR' | 'DISC',
            takerMarkets: [takerMarket],
          });
        }
      }

      const preferredGroups = [...preferredGroupMap.values()];

      const preferredResults = await Promise.allSettled(
        preferredGroups.map((group) =>
          submitPreferredTermOffer({
            takerMarkets: group.takerMarkets,
            rate: group.rate,
            rateType: group.rateType,
            expirationDate,
            ...(autoRenewalChecked && {
              renewalInformation: { enabled: true, termDays: RENEWAL_TERM_DAYS },
            }),
          })
        )
      );

      const preferredSuccesses: TakerMarket[] = [];
      const preferredSuccessMarkets: { tm: TakerMarket; rate: number; rateType: string }[] = [];

      preferredResults.forEach((result, idx) => {
        const group = preferredGroups[idx];
        if (result.status === 'fulfilled') {
          group.takerMarkets.forEach((tm) => {
            preferredSuccesses.push(tm);
            preferredSuccessMarkets.push({ tm, rate: group.rate, rateType: group.rateType });
          });
        } else {
          addError('Enterprise Offer Builder Preferred Term Submit Error', {
            error: result.reason,
            location: 'OfferSubmit component -> handleSubmit handler',
            rate: group.rate,
            rateType: group.rateType,
          });
        }
      });

      if (preferredSuccesses.length > 0) {
        const preferredEndTime = performance.now();
        const submissionDurationMs = Math.round(preferredEndTime - preferredStartTime);

        preferredSuccessMarkets.forEach(({ tm, rate, rateType }) => {
          const marketStats = computeMarketSubmitStats({
            takerMarket: tm,
            rate,
            rateType: rateType as 'APR' | 'DISC',
            exchangeRates,
          });
          // preUpgradeRates holds each market's strategy from immediately before the upgrade
          const originallySelectedPriceStrategy = preUpgradeRates?.find(
            (r) => r.takerMarketKey === buildTakerMarketKey(tm)
          )?.rateInfo.pricingStrategy;
          trackOfferSubmitEvent({
            pricingType: 'PREFERRED',
            marketUuid: tm.marketUuid,
            supplierDivisionUuid: tm.takerDivisionUuid,
            submissionBatchId: preferredSubmissionBatchId,
            isDraft: false,
            isEdit: !!(tm.offerConfig.uuid && tm.offerConfig.isEnabled),
            isEnabled: true,
            isManuallyAwarded: tm.offerConfig.isManuallyAwarded ?? false,
            marketStats,
            extras: {
              rate,
              rateType,
              originallySelectedFrequency: frequency,
              referrer: 'offer-builder',
              autoRenewalEnabled: autoRenewalChecked,
              submissionDurationMs,
              ...(originallySelectedPriceStrategy && { originallySelectedPriceStrategy }),
              ...(location && { location }),
              ...(source && { source }),
            },
            trackEnterpriseEvent,
          });
        });

        const preferredSuccessStats = calculateStatsForMarkets(preferredSuccesses);
        trackBulkOfferStats({
          stats: preferredSuccessStats,
          selectedCurrency,
          exchangeRates,
          location,
          source,
          submissionDurationMs,
          takerMarketCount: preferredSuccesses.length,
          trackEnterpriseEvent,
          builderVariant: 'standard',
          submissionBatchId: preferredSubmissionBatchId,
          preferredRateUpgrade: preferredRateSelected,
          blendedRate: calculateBlendedRateForMarkets(preferredSuccesses),
          blendedRateType: displayRateType,
        });
        navigate(asEnterpriseLink('build-offer/confirmation'));
        dispatch({ type: 'SET_SUBMISSION_COMPLETE', payload: { errors: [] } });
        dispatch({ type: 'CLEAR_PREFERRED_RATE_STATE' });
      } else {
        setIsErrorModalOpen(true);
      }
    } else {
      const instantPayEligibleKeys = new Set(
        effectiveRates
          .filter(({ takerMarket }) => isInstantPayEligible(takerMarket))
          .map(({ takerMarket }) => buildTakerMarketKey(takerMarket))
      );
      const submitValues = mapSelectedToSubmitValues({
        effectiveRates,
        frequency,
        scheduleData: schedule,
        instantPayEligibleKeys,
      });
      const submitStartTime = performance.now();
      const { errors, successes, submissionBatchId } = await submitOffers(submitValues);
      const submitEndTime = performance.now();
      const submissionDurationMs = Math.round(submitEndTime - submitStartTime);

      successes.forEach((success) => {
        const workingTakerMarketData = mapSuccessToWorkingTakerMarket({
          marketUuid: success.payload.marketUuid,
          takerDivisionUuid: success.payload.supplierDivisionUuid,
        });
        const effectiveRateEntry = effectiveRates.find(
          ({ takerMarket: tm }) =>
            tm.marketUuid === success.payload.marketUuid &&
            tm.takerDivisionUuid === success.payload.supplierDivisionUuid
        );
        const marketStats =
          effectiveRateEntry?.takerMarket && effectiveRateEntry.rate !== undefined && effectiveRateEntry.rateType
            ? computeMarketSubmitStats({
                takerMarket: effectiveRateEntry.takerMarket,
                rate: effectiveRateEntry.rate,
                rateType: effectiveRateEntry.rateType as 'APR' | 'DISC',
                exchangeRates,
              })
            : {};
        const trackEventPayload = {
          ...success.payload,
          ...marketStats,
          frequency,
          referrer: 'offer-builder',
          isDraft: false,
          isEdit: !!(
            effectiveRateEntry?.takerMarket.offerConfig.uuid && effectiveRateEntry?.takerMarket.offerConfig.isEnabled
          ),
          isEnabled: true,
          isManuallyAwarded: effectiveRateEntry?.takerMarket.offerConfig.isManuallyAwarded ?? false,
          isPreferred: effectiveRateEntry?.takerMarket.offerConfig.offerType === 'PREFERRED_TERM',
          isInstantPay: success.submitType === 'INSTANT_PAY',
          ...(location && { location }),
          ...(source && { source }),
          ...(isAcquisitionVariant &&
            acquisitionOfferProperties?.acquisitionCorrelationId && {
              acquisitionCorrelationId: acquisitionOfferProperties.acquisitionCorrelationId,
            }),
          ...(isAcquisitionVariant &&
            acquisitionOfferProperties?.urlReferrer && { urlReferrer: acquisitionOfferProperties.urlReferrer }),
          ...(workingTakerMarketData ?? {}),
          // effectiveRateEntry provides rate/rateType for markets where workingTakerMarkets doesn't store them (e.g. BENCHMARK)
          ...(effectiveRateEntry?.rate !== undefined && {
            rate: Math.round(effectiveRateEntry.rate * 100) / 100,
          }),
          ...(effectiveRateEntry?.rateType !== undefined && { rateType: effectiveRateEntry.rateType }),
          submissionDurationMs: success.submissionDurationMs,
          submissionBatchId,
        };

        if (
          success.submitType === 'NAME_YOUR_RATE' ||
          success.submitType === 'EXPRESS_ACCEPT' ||
          success.submitType === 'INSTANT_PAY'
        ) {
          trackEnterpriseEvent(`offer::submitted`, trackEventPayload);
        } else if (success.submitType === 'BENCHMARK') {
          trackEnterpriseEvent(`offer::variable-rate::submitted`, trackEventPayload);
        } else if (success.submitType === 'FIXED') {
          trackEnterpriseEvent(`offer::fixed-rate::submitted`, trackEventPayload);
        }
      });

      const successfulSelectedMarkets = successes
        .map((success) => {
          return selected.find(
            (market) =>
              market.marketUuid === success.payload.marketUuid &&
              market.takerDivisionUuid === success.payload.supplierDivisionUuid
          );
        })
        .filter((market): market is (typeof selected)[0] => market !== undefined);

      // calculate stats only for successful submissions
      const successStats = calculateStatsForMarkets(successfulSelectedMarkets);

      if (successfulSelectedMarkets.length > 0) {
        trackBulkOfferStats({
          stats: successStats,
          selectedCurrency,
          exchangeRates,
          location,
          source,
          acquisitionOfferProperties,
          submissionDurationMs,
          takerMarketCount: successfulSelectedMarkets.length,
          trackEnterpriseEvent,
          builderVariant: 'standard',
          submissionBatchId,
          preferredRateUpgrade: preferredRateSelected,
          blendedRate: calculateBlendedRateForMarkets(successfulSelectedMarkets),
          blendedRateType: displayRateType,
        });
      }

      if (errors.length > 0) {
        errors.forEach((error) => {
          addError('Enterprise Offer Builder Submit Error', {
            error: error.error,
            location: 'OfferSubmit component -> handleSubmit handler',
            payload: error.payload,
            builderVariant,
          });
        });
      }

      if (shouldSubmitUpdate(isIncludeNewBuyerChecked)) {
        await updateIncludeNewBuyer(isIncludeNewBuyerChecked);

        trackEnterpriseEvent('auto-setup-offers-for-new-customers::submit', {
          referrer: 'offer-review',
          toggleValue: isIncludeNewBuyerChecked,
        });
      }

      // if all offers failed, show error modal instead of navigating
      if (errors.length === selected.length && successes.length === 0) {
        setIsErrorModalOpen(true);
      } else {
        if (isAcquisitionVariant) {
          navigate(asEnterpriseLink('offer/acquisition/confirmation'));
        } else {
          navigate(asEnterpriseLink('build-offer/confirmation'));
        }

        dispatch({ type: 'SET_SUBMISSION_COMPLETE', payload: { errors } });
      }
    }

    setOfferSubmitting(false);
  };

  const onAgreementsChange = (value: boolean) => {
    setIsAgreementsChecked(value);
    setHasAgreementsError(false);
  };

  return (
    <>
      <FinalApprovalModal
        isFinalApprovalModalOpen={isFinalApprovalModalOpen}
        setIsFinalApprovalModalOpen={setIsFinalApprovalModalOpen}
        onFinalApproval={handleSubmit}
        isSubmittingDraftOffer={offerSubmitting}
        isSubmitError={isErrorModalOpen}
        isAgreementsErrorNotChecked={hasAgreementsError}
      />
      <OfferSubmitErrorModal isOpen={isErrorModalOpen} setIsOpen={setIsErrorModalOpen} />
      <div className="w-full">
        <div className="flex w-full flex-col space-y-4 rounded-xl bg-secondary-500 p-6 text-white">
          <div>
            <div className="text-lg font-medium">{t('core.estimatedEarlyPaymentAmount')}</div>
            <div className="text-4xl font-bold">
              {asCurrency(submittedOfferStats.estimatedEarlyPaymentAmount, selectedCurrency)}
            </div>
          </div>
          <div className="space-y-3 rounded-md bg-white p-4 text-text-primary">
            <div className="flex flex-row flex-wrap items-center gap-x-10 gap-y-2">
              <div className="space-y-0.5">
                <BuyerLogoList takerMarkets={selected} />
                <div className="text-sm text-text-secondary">{t('core.yourOffer')}</div>
              </div>
              <div className="ml-auto shrink-0 font-serif text-xl font-bold">
                <BlendedRateDisplay takerMarkets={selected} />
              </div>
              {(draftOfferProperties?.offerType === 'PREFERRED' || preferredRateSelected) && (
                <PreferredAutoRenewal autoRenewal={autoRenewalChecked} setIsRenewalChecked={setAutoRenewalChecked} />
              )}
            </div>
          </div>
          {showIncludeNewBuyer && (
            <IncludeNewBuyer checked={isIncludeNewBuyerChecked} onChange={setIsIncludeNewBuyerChecked} />
          )}
          <ConfirmAgreements
            checked={isAgreementsChecked}
            disabled={offerSubmitting}
            hasError={hasAgreementsError}
            onChange={onAgreementsChange}
            takersMarkets={selected}
            variant="light"
          />
          <div className="flex justify-end" ref={intersectionRef}>
            <Button
              className="w-full sm:w-fit"
              disabled={disableSubmitButton}
              inverse
              loading={offerSubmitting}
              onClick={handleSubmit}
              variant="cta"
              data-intercom-target={'offerBuilderSubmit' satisfies IntercomDataTargetNames}
            >
              {t('builder.submitEarlyPayment')}
            </Button>
          </div>
        </div>
      </div>
      {isAboveViewport && (
        <PageBottomPortal>
          <NavigationBar className="animate-[slide-up_0.3s_ease-out]">
            <div className="flex flex-col items-center justify-between gap-4 md:flex-row md:gap-10">
              <ConfirmAgreements
                checked={isAgreementsChecked}
                disabled={offerSubmitting}
                hasError={hasAgreementsError}
                onChange={onAgreementsChange}
                takersMarkets={selected}
                variant="dark"
              />
              <div className="flex w-full shrink-0 flex-col items-center gap-4 md:w-fit md:flex-row">
                {!disableNavigation && (
                  <ButtonLink
                    className="w-full md:w-fit"
                    disabled={offerSubmitting}
                    to={asEnterpriseLink('build-offer/rate')}
                    variant="tertiary"
                  >
                    {t('core.back')}
                  </ButtonLink>
                )}
                <Button
                  className="w-full md:w-fit"
                  disabled={disableSubmitButton}
                  loading={offerSubmitting}
                  onClick={handleSubmit}
                  variant="cta"
                >
                  {t('builder.submitEarlyPayment')}
                </Button>
              </div>
            </div>
          </NavigationBar>
        </PageBottomPortal>
      )}
    </>
  );
};

export default OfferSubmit;
